import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Plus, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FrequentlyBundled({ outfitId, onAddBundle }) {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [addedIds, setAddedIds] = useState(new Set());

  useEffect(() => {
    if (!outfitId) return;
    fetch(`${API}/outfits/${outfitId}/frequently-bundled?limit=4`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setEnabled(d.enabled !== false);
      })
      .catch(() => {});
  }, [outfitId]);

  if (!enabled || items.length === 0) return null;

  const handleToggle = (item) => {
    setAddedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
        onAddBundle?.(item, false);
      } else {
        next.add(item.id);
        onAddBundle?.(item, true);
      }
      return next;
    });
  };

  return (
    <section className="mt-10 lg:mt-14" data-testid="frequently-bundled-section">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
        <h2 className="text-lg lg:text-xl text-[#282828] font-light">Frequently Bundled Together</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
        {items.map((item) => {
          const isAdded = addedIds.has(item.id);
          return (
            <div key={item.id} className="group relative" data-testid={`freq-bundle-${item.id}`}>
              <Link to={`/outfit/${item.id}`} className="block">
                <div className="relative overflow-hidden bg-[#f7f5f4]" style={{ height: 0, paddingBottom: "133%" }}>
                  <img
                    src={item.images?.[0]}
                    alt={item.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    style={{ objectPosition: `${(item.image_focal_points || {})[item.images?.[0]]?.x ?? 50}% ${(item.image_focal_points || {})[item.images?.[0]]?.y ?? 50}%` }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  {item.bundle_count > 0 && (
                    <div className="absolute top-2 left-2 bg-[#9A7B4F]/90 text-white text-[9px] tracking-[0.05em] px-2 py-0.5 z-10">
                      Bundled {item.bundle_count}x
                    </div>
                  )}
                </div>
              </Link>
              <div className="pt-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-[#282828] truncate">{item.name}</p>
                  <p className="text-[11px] text-[#676664] font-light mt-0.5">From ${item.price_4_day || item.price_per_day || 0} / rental</p>
                </div>
                <button
                  onClick={() => handleToggle(item)}
                  className={`flex-shrink-0 w-7 h-7 flex items-center justify-center border transition-all ${
                    isAdded
                      ? "bg-[#9A7B4F] border-[#9A7B4F] text-white"
                      : "border-[#282828]/20 text-[#282828]/50 hover:border-[#9A7B4F] hover:text-[#9A7B4F]"
                  }`}
                  data-testid={`add-freq-bundle-${item.id}`}
                >
                  {isAdded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
